/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ShieldCheck, ArrowLeft, Package } from "lucide-react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import AdminNavTabs from "@/components/AdminNavTabs";
import { fetchAdminHandoverList, fetchAdminHandover, prepareHandover, approveHandover, generateHandoverDownloadLink } from "@/lib/api";

const STATUS_LABEL = {
  not_eligible: "Not Eligible", eligible: "Eligible", preparing: "Preparing",
  validation_failed: "Validation Failed", ready_for_review: "Ready for Review",
  approved: "Approved", download_available: "Download Available", downloaded: "Downloaded",
};
const STATUS_TONE = {
  not_eligible: "text-slate-400", eligible: "text-emerald-600", validation_failed: "text-red-600",
  ready_for_review: "text-amber-600", approved: "text-emerald-600",
  download_available: "text-emerald-600", downloaded: "text-slate-600",
};

export function AdminHandoverList() {
  const { token, email, checking, isLoggedIn, revealAdminUi } = useAdminAuth();
  const [items, setItems] = useState(null);

  useEffect(() => { if (token) fetchAdminHandoverList(token).then(setItems).catch(() => {}); }, [token]);

  if (checking) return null;
  if (!revealAdminUi) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-16 text-slate-900">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center gap-2.5 text-emerald-700"><ShieldCheck size={20} /><span className="text-xs font-bold uppercase tracking-[0.16em]">{isLoggedIn ? `Logged in as ${email}` : "DEBUG — Admin UI revealed"}</span></div>
        <h1 className="mt-4 font-display text-3xl font-bold">Admin</h1>
        <AdminNavTabs />

        <h2 className="mt-8 flex items-center gap-2 text-xl font-bold font-display"><Package size={20} className="text-emerald-600" /> Website Handover</h2>
        <p className="mt-1 text-sm text-slate-500">Sites with a paid Website Ownership order are eligible for a standalone handover package.</p>

        <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden overflow-x-auto" data-testid="handover-list">
          {!items ? (
            <p className="p-6 text-sm text-slate-500">Loading…</p>
          ) : items.filter((i) => i.ownership_eligible).length === 0 ? (
            <p className="p-6 text-sm text-slate-500">No sites with a paid Website Ownership order yet.</p>
          ) : (
            <table className="w-full min-w-[500px] text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-2.5">Business</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Package</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {items.filter((i) => i.ownership_eligible).map((i) => (
                  <tr key={i.website_slug}>
                    <td className="px-4 py-3"><Link to={`/admin/handover/${i.website_slug}`} className="font-medium text-emerald-700 hover:underline">{i.business_name}</Link></td>
                    <td className={`px-4 py-3 font-medium ${STATUS_TONE[i.status] || ""}`}>{STATUS_LABEL[i.status] || i.status}</td>
                    <td className="px-4 py-3 text-slate-500">{i.package_version || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export function AdminHandoverDetail() {
  const { slug } = useParams();
  const { token, checking, revealAdminUi } = useAdminAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [downloadLink, setDownloadLink] = useState(null);

  const load = () => { if (token) fetchAdminHandover(token, slug).then(setData).catch(() => {}); };
  useEffect(load, [token, slug]);

  if (checking) return null;
  if (!revealAdminUi) return <Navigate to="/admin/login" replace />;
  if (!data) return <div className="min-h-screen bg-slate-50 px-4 py-16 text-slate-500">Loading…</div>;

  const pkg = data.package;
  const act = (fn) => async () => {
    setError("");
    try { await fn(token, slug); load(); } catch (err) { setError(err.response?.data?.detail || "Action failed."); }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-16 text-slate-900">
      <div className="mx-auto max-w-2xl">
        <Link to="/admin/handover" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} /> All handovers</Link>
        <h1 className="mt-2 font-display text-2xl font-bold">{slug}</h1>

        <div className="mt-4 rounded-xl border border-slate-200 bg-white p-6">
          <p className="text-sm text-slate-600">Ownership eligible: <strong>{data.ownership_eligible ? "Yes" : "No"}</strong></p>
          {pkg && (
            <>
              <p className="mt-1 text-sm text-slate-600">Status: <strong className={STATUS_TONE[pkg.status]}>{STATUS_LABEL[pkg.status] || pkg.status}</strong></p>
              <p className="mt-1 text-sm text-slate-600">Package version: <strong>{pkg.package_version}</strong></p>
              <p className="mt-1 text-sm text-slate-600">Assisted Setup & Launch purchased: <strong>{pkg.assisted_setup_purchased ? "Yes" : "No"}</strong></p>
              {pkg.validation_errors?.length > 0 && (
                <div className="mt-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">
                  {pkg.validation_errors.map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}
              {pkg.manifest && (
                <p className="mt-2 text-xs text-slate-500">Modules: {pkg.manifest.enabled_modules.join(", ") || "none"} · Data counts: {Object.entries(pkg.manifest.data_counts).map(([k, v]) => `${k}: ${v}`).join(", ")}</p>
              )}
              {pkg.download_count > 0 && <p className="mt-1 text-xs text-slate-500">Downloaded {pkg.download_count} time(s){pkg.last_downloaded_at ? `, last on ${new Date(pkg.last_downloaded_at).toLocaleString()}` : ""}.</p>}
            </>
          )}

          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

          <div className="mt-4 flex gap-2 flex-wrap">
            <button onClick={act(prepareHandover)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-100">{pkg ? "Re-prepare Package" : "Prepare Handover"}</button>
            {pkg?.status === "ready_for_review" && <button onClick={act(approveHandover)} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white">Approve</button>}
            {["approved", "download_available", "downloaded"].includes(pkg?.status) && (
              <button onClick={async () => { setError(""); try { const r = await generateHandoverDownloadLink(token, slug); setDownloadLink(r); load(); } catch (err) { setError(err.response?.data?.detail || "Action failed."); } }}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-100">Generate Download Link</button>
            )}
          </div>

          {downloadLink && (
            <p className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              Download link generated (expires {new Date(downloadLink.expires_at).toLocaleString()}). Share this with the customer — it is not shown again after you leave this page: <code className="break-all">/api/handover/download/{downloadLink.download_token}</code>
            </p>
          )}
        </div>

        <div className="mt-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Audit trail</p>
          <ul className="space-y-1 text-sm text-slate-600">
            {data.events.map((e, i) => <li key={i}>{new Date(e.at).toLocaleString()} — {e.event.replace(/_/g, " ")}{e.actor ? ` (${e.actor})` : ""}</li>)}
          </ul>
        </div>
      </div>
    </div>
  );
}
